/**
 * analytics.js — organic-search-suite GA4 events SSOT (spec v2.1.0)
 *
 * Single place that fires the canonical `pseo_*` custom events. The GA4 property
 * has 16 event-scoped custom dimensions + 3 key events provisioned to match this
 * (see organic-search-suite/events_spec.py + pseo-handoff.json). DO NOT invent new
 * event/param names here without bumping the spec + re-provisioning.
 *
 * Page identity (page_type/keyword/geo/city/service_line/url/canonical/language)
 * is set ONCE per route via setPageContext() and merged into every event so we
 * never have to thread it through each click handler.
 *
 * lead_channel provenance (owner decision): a click on an attorney/law-firm
 * referral CTA sets the in-session lead_channel to 'attorney_referral'. Any
 * subsequent dual-use form submit / phone click inherits that until it's reset
 * (e.g. patient flow) or the session ends. Default 'patient'.
 */

const PROJECT_ID = 'pseo-ortho';
const SPEC_VERSION = '2.1.0';

// ── page-identity context (set per route) ──────────────────────────────────
let pageCtx = {
  project_id: PROJECT_ID,
  client_id: '',
  page_type: '',
  keyword: '',
  geo: '',
  language: 'en',
  url: '',
  canonical_url: '',
  service_line: '',
  city: '',
  state: '',
};

// ── lead_channel session provenance ─────────────────────────────────────────
const LEAD_KEY = '__pseo_lead_channel__';
function getLeadChannel() {
  try {
    return window.sessionStorage.getItem(LEAD_KEY) || 'patient';
  } catch (e) {
    return window.__pseo_lead_channel_mem || 'patient';
  }
}
export function setLeadChannel(channel) {
  // channel: 'attorney_referral' | 'patient'
  try {
    window.sessionStorage.setItem(LEAD_KEY, channel);
  } catch (e) {
    window.__pseo_lead_channel_mem = channel;
  }
}
export function markAttorneyReferral() {
  setLeadChannel('attorney_referral');
}

/**
 * setPageContext — call once per route render (e.g. in PageMeta) with whatever
 * page-identity fields are known. Unknown fields keep their prior/empty value.
 * Also pushes a dataLayer page-context object so GTM-side tags can inherit it.
 */
export function setPageContext(ctx = {}) {
  const brand = ctx.brand || {};
  const next = {
    ...pageCtx,
    project_id: PROJECT_ID,
    client_id: ctx.client_id || brand.brandKey || pageCtx.client_id,
    state: ctx.state || brand.state || pageCtx.state,
  };
  if (!ctx.geo && (ctx.state || brand.state)) next.geo = `US-${ctx.state || brand.state}`;
  // copy through any explicitly-provided identity fields
  [
    'page_type', 'keyword', 'language', 'url', 'canonical_url',
    'service_line', 'city', 'geo',
  ].forEach((k) => {
    if (ctx[k] !== undefined && ctx[k] !== null && ctx[k] !== '') next[k] = ctx[k];
  });
  if (!next.url && typeof window !== 'undefined') next.url = window.location.href;
  pageCtx = next;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ pseo_page_context: { ...pageCtx, spec_version: SPEC_VERSION } });
  } catch (e) { /* noop */ }
  return pageCtx;
}

export function getPageContext() {
  return { ...pageCtx };
}

// ── core emitter ─────────────────────────────────────────────────────────────
function emit(eventName, extra = {}) {
  if (typeof window === 'undefined') return;
  const params = { ...pageCtx, ...extra };
  // never send undefined; GA4 wants strings
  Object.keys(params).forEach((k) => {
    if (params[k] === undefined || params[k] === null) params[k] = '';
    else if (typeof params[k] !== 'string') params[k] = String(params[k]);
  });
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    } else {
      // gtag not loaded yet (deferred until post-LCP). Push an arguments-shaped
      // entry — gtag.js only replays `arguments` objects, so a plain array was
      // silently dropped on replay (2026-07-21 audit L1).
      window.dataLayer = window.dataLayer || [];
      (function () { window.dataLayer.push(arguments); })('event', eventName, params);
    }
  } catch (e) { /* analytics must never break UX */ }
}

// ── canonical events (spec v2.1.0) ───────────────────────────────────────────
export function trackPageView(ctx) {
  if (ctx) setPageContext(ctx);
  emit('pseo_page_view_enriched');
}

export function trackCtaClick({ cta_id = '', cta_text = '', lead_channel } = {}) {
  if (lead_channel) setLeadChannel(lead_channel);
  emit('pseo_cta_click', { cta_id, cta_text, lead_channel: lead_channel || getLeadChannel() });
}

export function trackPhoneClick({ cta_id = 'phone', cta_text = '' } = {}) {
  emit('pseo_phone_click', { cta_id, cta_text, lead_channel: getLeadChannel() }); // KEY EVENT
}

export function trackFormStart({ cta_id = '' } = {}) {
  emit('pseo_form_start', { cta_id, lead_channel: getLeadChannel() });
}

export function trackFormSubmit({ cta_id = '', lead_channel } = {}) {
  emit('pseo_form_submit', { cta_id, lead_channel: lead_channel || getLeadChannel() }); // KEY EVENT
}

export function trackVideoPlay({ cta_id = 'video', video_id = '', video_title = '' } = {}) {
  emit('pseo_video_play', { cta_id, video_id, video_title }); // KEY EVENT
}

export function trackLocationClick({ cta_id = 'location', cta_text = '' } = {}) {
  emit('pseo_location_click', { cta_id, cta_text });
}

export function trackRelatedPageClick({ cta_id = '' } = {}) {
  emit('pseo_related_page_click', { cta_id });
}

export function trackScrollDepth(percent) {
  emit('pseo_scroll_depth', { cta_id: String(percent) });
}

// ── scroll-depth auto-tracker (call once per page) ───────────────────────────
let scrollBound = false;
const scrollHit = {};
export function initScrollDepth(milestones = [25, 50, 75, 100]) {
  if (typeof window === 'undefined' || scrollBound) return;
  scrollBound = true;
  const onScroll = () => {
    const doc = document.documentElement;
    const scrolled = (window.scrollY || doc.scrollTop || 0) + window.innerHeight;
    const height = doc.scrollHeight || 1;
    const pct = Math.round((scrolled / height) * 100);
    milestones.forEach((m) => {
      if (pct >= m && !scrollHit[m]) {
        scrollHit[m] = true;
        trackScrollDepth(m);
      }
    });
    if (scrollHit[milestones[milestones.length - 1]]) {
      window.removeEventListener('scroll', onScroll);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
}

// reset per-page scroll milestones on route change
export function resetScrollDepth() {
  Object.keys(scrollHit).forEach((k) => delete scrollHit[k]);
}

const analytics = {
  setPageContext, getPageContext, setLeadChannel, markAttorneyReferral,
  trackPageView, trackCtaClick, trackPhoneClick, trackFormStart, trackFormSubmit,
  trackVideoPlay, trackLocationClick, trackRelatedPageClick, trackScrollDepth,
  initScrollDepth, resetScrollDepth,
};
export default analytics;
